import React, { useState, useCallback } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import './AdminLoginModal.css';

interface AdminLoginModalProps {
  isOpen: boolean;
  onClose?: () => void;
}

/**
 * Modal para login de administrador
 * Pide email y contraseña, autentica contra POST /admin/login
 */
export const AdminLoginModal: React.FC<AdminLoginModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { login, error, isLoading } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLocalError(null);

      if (!email.trim() || !password.trim()) {
        setLocalError('Por favor ingresa email y contraseña');
        return;
      }

      try {
        await login(email.trim(), password);
        window.location.href = '/admin';
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setLocalError(errorMessage);
      }
    },
    [email, password, login]
  );

  const handleClear = useCallback(() => {
    setEmail('');
    setPassword('');
    setLocalError(null);
  }, []);

  const displayError = localError || error;

  if (!isOpen) {
    return null;
  }

  return (
    <div className="admin-login-modal-overlay">
      <div className="admin-login-modal">
        <div className="admin-login-modal-header">
          <h2>Acceso de Administrador</h2>
          {onClose && (
            <button
              className="admin-login-modal-close"
              onClick={onClose}
              aria-label="Cerrar modal"
            >
              ×
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="admin-login-modal-form">
          <div className="admin-login-form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              disabled={isLoading}
              autoFocus
            />
          </div>

          <div className="admin-login-form-group">
            <label htmlFor="password">Contraseña</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Tu contraseña segura"
              disabled={isLoading}
            />
          </div>

          {displayError && (
            <div className="admin-login-modal-error">
              <p>{displayError}</p>
            </div>
          )}

          <div className="admin-login-modal-actions">
            <button
              type="submit"
              disabled={isLoading || !email.trim() || !password.trim()}
              className="admin-login-modal-submit"
            >
              {isLoading ? 'Ingresando...' : 'Ingresar'}
            </button>
            <button
              type="button"
              onClick={handleClear}
              disabled={isLoading}
              className="admin-login-modal-clear"
            >
              Limpiar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminLoginModal;
